import { useCallback, useEffect, useState } from 'react';
import { Home } from './ui/Home.tsx';
import { RoomScreen } from './ui/RoomScreen.tsx';
import { MatchScreen } from './ui/MatchScreen.tsx';
import { ReplayScreen } from './ui/ReplayScreen.tsx';
import { HistoryScreen } from './ui/HistoryScreen.tsx';
import { LeaderboardScreen } from './ui/LeaderboardScreen.tsx';
import { ProfileScreen } from './ui/ProfileScreen.tsx';
import { PrivacyScreen } from './ui/PrivacyScreen.tsx';
import { BottomNav } from './ui/BottomNav.tsx';
import { DevPanel } from './ui/DevPanel.tsx';
import { FriendsScreen } from './ui/FriendsScreen.tsx';
import { NotificationsScreen } from './ui/NotificationsScreen.tsx';
import { ChatScreen } from './ui/ChatScreen.tsx';

type Route =
  | { name: 'home' }
  | { name: 'room'; code: string }
  | { name: 'match'; matchId: string }
  | { name: 'replay'; matchId: string }
  | { name: 'history' }
  | { name: 'leaderboard' }
  | { name: 'profile' }
  | { name: 'friends' }
  | { name: 'notifications' }
  | { name: 'chat' }
  | { name: 'privacy' }
  | { name: 'dev' };

function parseRoute(pathname: string): Route {
  const routePath = pathname.split(/[?#]/, 1)[0] || '/';
  const room = /^\/r\/([A-Za-z0-9]{4,10})$/.exec(routePath);
  if (room?.[1]) return { name: 'room', code: room[1].toUpperCase() };
  const match = /^\/(?:m|matches)\/([0-9a-zA-Z_-]+)$/.exec(routePath);
  if (match?.[1]) return { name: 'match', matchId: match[1] };
  const replay = /^\/replay\/([0-9a-zA-Z_-]+)$/.exec(routePath);
  if (replay?.[1]) return { name: 'replay', matchId: replay[1] };
  if (routePath === '/history') return { name: 'history' };
  if (routePath === '/leaderboard') return { name: 'leaderboard' };
  if (routePath === '/profile') return { name: 'profile' };
  if (routePath === '/friends') return { name: 'friends' };
  if (routePath === '/notifications') return { name: 'notifications' };
  if (routePath === '/chat') return { name: 'chat' };
  if (routePath === '/privacy') return { name: 'privacy' };
  if (routePath === '/dev') return { name: 'dev' };
  return { name: 'home' };
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.pathname));
  const [currentPath, setCurrentPath] = useState(() => location.pathname);

  const navigate = useCallback((path: string): void => {
    history.pushState(null, '', path);
    setCurrentPath(path);
    setRoute(parseRoute(path));
  }, []);

  useEffect(() => {
    const onPop = (): void => {
      setCurrentPath(location.pathname);
      setRoute(parseRoute(location.pathname));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const showBottomNav =
    route.name === 'home' ||
    route.name === 'history' ||
    route.name === 'leaderboard' ||
    route.name === 'profile';
  const withBottomNav = showBottomNav || route.name === 'friends' || route.name === 'notifications' || route.name === 'chat';

  let content = <Home navigate={navigate} />;

  switch (route.name) {
    case 'room':
      content = <RoomScreen code={route.code} navigate={navigate} />;
      break;
    case 'match':
      content = <MatchScreen matchId={route.matchId} navigate={navigate} />;
      break;
    case 'replay':
      content = <ReplayScreen matchId={route.matchId} navigate={navigate} />;
      break;
    case 'history':
      content = <HistoryScreen navigate={navigate} />;
      break;
    case 'leaderboard':
      content = <LeaderboardScreen navigate={navigate} />;
      break;
    case 'profile':
      content = <ProfileScreen navigate={navigate} />;
      break;
    case 'friends':
      content = <FriendsScreen navigate={navigate} />;
      break;
    case 'notifications':
      content = <NotificationsScreen navigate={navigate} />;
      break;
    case 'chat':
      content = <ChatScreen navigate={navigate} />;
      break;
    case 'privacy':
      content = <PrivacyScreen navigate={navigate} />;
      break;
    case 'dev':
      content = <DevPanel navigate={navigate} />;
      break;
    default:
      content = <Home navigate={navigate} />;
      break;
  }

  return (
    <div className={`app-root ${withBottomNav ? 'has-bottom-nav' : ''}`}>
      {content}
      {withBottomNav && <BottomNav currentRoute={currentPath} navigate={navigate} />}
    </div>
  );
}
